import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * Outbound actions audit (Action Gateway) and the close-gate dry run.
 */

export type ActionDecision = 'executed' | 'shadowed' | 'denied' | 'deduped' | 'deferred' | 'failed';

export interface OutboundAction {
  _id: string;
  type: string;
  target: { system: 'gitlab' | 'rocketchat'; gitlabProjectId?: number; ref?: string };
  requestedBy: { kind: string; id?: string; username?: string; role?: string };
  reason: string;
  mode: 'shadow' | 'live';
  decision: ActionDecision;
  denyReason?: string;
  lintNotes?: string[];
  payload: Record<string, unknown>;
  createdAt: string;
  executedAt?: string;
  url?: string;
  error?: string;
}

export interface DodReport {
  byProfile: Record<string, { checked: number; wouldReopen: number }>;
  byCode: Record<string, number>;
  issues: Array<{
    ref: string;
    gitlabProjectId: number;
    iid: number;
    title: string;
    closedAt?: string;
    profile: string;
    passed: boolean;
    missing: string[];
    warnings: string[];
  }>;
}

export interface KillSwitch {
  global: boolean;
  projects: number[];
}

export async function listActions(params: { decision?: string; type?: string; limit?: number } = {}): Promise<OutboundAction[]> {
  const response = await apiClient.get<ApiResponse<OutboundAction[]>>('/actions', { params });
  return response.data.data || [];
}

export async function approveAction(id: string): Promise<OutboundAction> {
  const response = await apiClient.post<ApiResponse<OutboundAction>>(`/actions/${id}/approve`);
  return response.data.data as OutboundAction;
}

export async function getKillSwitch(): Promise<KillSwitch> {
  const response = await apiClient.get<ApiResponse<KillSwitch>>('/actions/kill-switch');
  return response.data.data as KillSwitch;
}

export async function setKillSwitch(enabled: boolean): Promise<void> {
  await apiClient.post('/actions/kill-switch', { enabled });
}

export async function getDodReport(project?: string): Promise<DodReport> {
  const response = await apiClient.get<ApiResponse<DodReport>>('/issue-memory/dod-report', { params: { project, limit: 1000 } });
  return response.data.data as DodReport;
}

export interface LabelProposal {
  ref: string;
  gitlabProjectId: number;
  iid: number;
  title: string;
  labels: string[];
  add: string[];
  remove: string[];
}

export async function getLabelPlan(): Promise<LabelProposal[]> {
  const response = await apiClient.get<ApiResponse<LabelProposal[]>>('/issue-memory/label-plan');
  return response.data.data || [];
}

export async function applyLabelPlan(): Promise<{ issues: number; decisions: Record<string, number> }> {
  const response = await apiClient.post<ApiResponse<{ issues: number; decisions: Record<string, number> }>>('/issue-memory/label-plan/apply', {});
  return response.data.data as { issues: number; decisions: Record<string, number> };
}

export interface LadderPreview {
  updates: Array<{ gitlabProjectId: number; iid: number; change: string; state: { level: number; waitingLevel: number; atRisk: boolean } }>;
  messages: Array<{ to: string; lines: string[] }>;
  comments: Array<{ gitlabProjectId: number; iid: number; body: string }>;
}

export async function getLadderPreview(date?: string): Promise<LadderPreview> {
  const response = await apiClient.get<ApiResponse<LadderPreview>>('/issue-memory/ladder-preview', { params: { date } });
  return response.data.data as LadderPreview;
}

export async function getDigestPreview(): Promise<string> {
  const response = await apiClient.get<string>('/issue-memory/digest-preview', { responseType: 'text' });
  return response.data;
}
